import React from 'react';
import styles from './Avatar.module.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarStatus = 'online' | 'offline' | 'busy' | 'away';

export interface AvatarProps {
  /**
   * Image source URL
   */
  src?: string;

  /**
   * Alt text for the image
   */
  alt?: string;

  /**
   * Name used to generate initials fallback
   */
  name?: string;

  /**
   * Size of the avatar
   */
  size?: AvatarSize;

  /**
   * Status indicator
   */
  status?: AvatarStatus;

  /**
   * Apply glassmorphism effect
   */
  glass?: boolean;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Click handler
   */
  onClick?: () => void;
}

/**
 * Avatar - A user profile image component with fallback and status indicators
 *
 * Displays user avatars with automatic fallback to initials, optional status
 * indicators, and glassmorphism border effects.
 *
 * @example
 * ```tsx
 * <Avatar
 *   src="https://example.com/avatar.jpg"
 *   name="John Doe"
 *   size="md"
 *   status="online"
 *   glass
 * />
 * ```
 */
export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  name,
  size = 'md',
  status,
  glass = true,
  className,
  onClick,
}) => {
  const [imageError, setImageError] = React.useState(false);

  const getInitials = (name?: string): string => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) {
      return parts[0]?.charAt(0).toUpperCase() || '';
    }
    const first = parts[0]?.charAt(0) || '';
    const last = parts[parts.length - 1]?.charAt(0) || '';
    return (first + last).toUpperCase();
  };

  const avatarClasses = [
    styles.avatar,
    styles[`size-${size}`],
    glass && styles.glass,
    onClick && styles.clickable,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const showImage = src && !imageError;
  const showInitials = !showImage && name;

  const content = (
    <>
      {showImage && (
        <img
          src={src}
          alt={alt || name || 'Avatar'}
          className={styles.image}
          onError={() => setImageError(true)}
        />
      )}
      {showInitials && (
        <span className={styles.initials} aria-label={name}>
          {getInitials(name)}
        </span>
      )}
      {!showImage && !showInitials && (
        <svg
          className={styles.placeholder}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
            fill="currentColor"
          />
        </svg>
      )}
      {status && (
        <span
          className={`${styles.status} ${styles[`status-${status}`]}`}
          aria-label={`Status: ${status}`}
        />
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={avatarClasses}
        onClick={onClick}
        aria-label={alt || name || 'Avatar'}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={avatarClasses}
      role="img"
      aria-label={alt || name || 'Avatar'}
    >
      {content}
    </div>
  );
};
